import { useRef } from "react";
import { Typography } from "@mui/material";
import { CoreButton, CoreChip, CoreInfoIndicator } from "../../core";
import i18nPackage from "../../i18n/i18n";
import { BasicStep } from "../components/basic-step";
import {
	BaseSubstepProps,
	BasicSubstep,
	BasicSubstepDescription,
	BasicSubstepTitle,
	getBasicSubstepIcon,
	SubstepStatus,
} from "../components/basic-substep";
import styles from "./index.module.css";
import { Trans } from "react-i18next";
import Guide from "../components/guide";

type ConnectionCheckSubstepProps = BaseSubstepProps & {
	minimumUploadSpeed?: number | string;
	userUploadSpeed?: number;
	onTryAgain: () => void;
};

export function ConnectionCheckSubstep({
	minimumUploadSpeed,
	userUploadSpeed,
	status,
	onTryAgain,
	rootRef,
}: ConnectionCheckSubstepProps) {
	const translationKey = status === "started" ? "unstarted" : status;

	const title = i18nPackage.t(`beforeQuiz.systemCheck.connectionCheck.title.${translationKey}`);
	const description = i18nPackage.t(`beforeQuiz.systemCheck.connectionCheck.description.${translationKey}`);

	return (
		<BasicSubstep className={styles.systemCheckSubstep} ref={rootRef}>
			{getBasicSubstepIcon(status)}
			<div className={styles.systemCheckContent}>
				<BasicSubstepTitle title={title} />
				<div className={styles.systemCheckButtonRow}>
					<BasicSubstepDescription description={description} />
					{status === "failed" && (
						<CoreButton className={styles.tryAgainButton} onClick={onTryAgain}>
							{i18nPackage.t("beforeQuiz.systemCheck.connectionCheck.tryAgainButton")}
						</CoreButton>
					)}
				</div>
				{status === "failed" && userUploadSpeed && minimumUploadSpeed && (
					<div className={styles.systemMetricsGrid}>
						<div className={styles.systemMetricsContainer}>
							<Typography className={styles.systemMetricsTitle}>
								{i18nPackage.t("beforeQuiz.systemCheck.connectionCheck.minimumUploadSpeedLabel")}
							</Typography>
							<Typography className={styles.systemMetricsValue} color="#525252">
								{i18nPackage.t("beforeQuiz.systemCheck.connectionCheck.uploadSpeedValue", {
									speed: minimumUploadSpeed,
								})}
							</Typography>
						</div>
						<div className={styles.systemMetricsContainer}>
							<div className={styles.systemMetricsTitleWrapper}>
								<Typography className={styles.systemMetricsTitle}>
									{i18nPackage.t("beforeQuiz.systemCheck.connectionCheck.uploadSpeedLabel")}
								</Typography>
								<CoreChip
									variant="rectangular"
									chipColor={"#FAE3CC"}
									className={`${styles.systemBadge} ${styles.failed}`}
								>
									{i18nPackage.t("beforeQuiz.systemCheck.fail")}
								</CoreChip>
							</div>
							<Typography className={styles.systemMetricsValue} color={"#C35100"}>
								<Trans
									i18nKey="beforeQuiz.systemCheck.connectionCheck.uploadSpeedValue"
									values={{ speed: `${userUploadSpeed === 0 ? "&lt;" : "~"}${userUploadSpeed}` }}
									shouldUnescape // Unescape the < sign.
									i18n={i18nPackage}
								/>
							</Typography>
						</div>
					</div>
				)}
			</div>
		</BasicSubstep>
	);
}

type SystemResourcesSubstepProps = BaseSubstepProps & {
	memoryUsage?: number;
	cpuUsage?: number;
	onCheck: () => void;
};

export function SystemResourcesSubstep({
	memoryUsage,
	cpuUsage,
	status,
	onCheck,
	rootRef,
}: SystemResourcesSubstepProps) {
	const tooltipContainerRef = useRef<HTMLDivElement>(null);
	const popperProps = { container: () => tooltipContainerRef.current };

	const translationKey = status === "started" ? "unstarted" : status;

	const title = i18nPackage.t(`beforeQuiz.systemCheck.systemResources.title.${translationKey}`);
	const description =
		status === "failed" ? (
			<>
				<Typography>
					<Trans
						i18n={i18nPackage}
						i18nKey={"beforeQuiz.systemCheck.systemResources.description.failed"}
						components={{ b: <strong /> }}
					/>
				</Typography>
				<Typography>{i18nPackage.t("beforeQuiz.systemCheck.systemResources.description.failed2")}</Typography>
			</>
		) : (
			i18nPackage.t(`beforeQuiz.systemCheck.systemResources.description.${translationKey}`)
		);

	const cpuPassed = !hasCpuUsageFailed(cpuUsage);
	const memoryPassed = !hasMemoryUsageFailed(memoryUsage);

	const cpuValueColor = cpuPassed ? "#48A600" : "#C35100";
	const memoryValueColor = memoryPassed ? "#48A600" : "#C35100";

	const cpuBadgeText = cpuPassed
		? i18nPackage.t("beforeQuiz.systemCheck.pass")
		: i18nPackage.t("beforeQuiz.systemCheck.fail");
	const memoryBadgeText = memoryPassed
		? i18nPackage.t("beforeQuiz.systemCheck.pass")
		: i18nPackage.t("beforeQuiz.systemCheck.fail");

	const cpuBadgeColor = cpuPassed ? "#48A6001A" : "#FAE3CC";
	const memoryBadgeColor = memoryPassed ? "#48A6001A" : "#FAE3CC";

	const cpuBadgeClassName = `${styles.systemBadge} ${cpuPassed ? styles.passed : styles.failed}`;
	const memoryBadgeClassName = `${styles.systemBadge} ${memoryPassed ? styles.passed : styles.failed}`;

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.systemCheckSubstep}>
				{getBasicSubstepIcon(status)}
				<div className={styles.systemCheckContent} ref={tooltipContainerRef}>
					<BasicSubstepTitle title={title} />
					<div className={styles.systemCheckButtonRow}>
						<BasicSubstepDescription description={description} />
						{status === "failed" && (
							<CoreButton className={styles.tryAgainButton} onClick={onCheck}>
								{i18nPackage.t("beforeQuiz.systemCheck.systemResources.reCheckButton")}
							</CoreButton>
						)}
					</div>
					{(status === "failed" || status === "completed") &&
						typeof cpuUsage === "number" &&
						typeof memoryUsage === "number" && (
							<div className={styles.systemMetricsGrid}>
								<div className={styles.systemMetricsContainer}>
									<div className={styles.systemMetricsTitleWrapper}>
										<div className={styles.systemMetricsTitleInfoWrapper}>
											<Typography className={styles.systemMetricsTitle}>
												{i18nPackage.t("beforeQuiz.systemCheck.systemResources.cpuUsage")}
											</Typography>
											<CoreInfoIndicator
												hint={i18nPackage.t("beforeQuiz.systemCheck.systemResources.cpuUsageTooltip")}
												size={20}
												color={"#565656"}
												PopperProps={popperProps}
											/>
										</div>
										<CoreChip variant="rectangular" chipColor={cpuBadgeColor} className={cpuBadgeClassName}>
											{cpuBadgeText}
										</CoreChip>
									</div>
									<Typography className={styles.systemMetricsValue} color={cpuValueColor}>{`${cpuUsage}%`}</Typography>
								</div>
								<div className={styles.systemMetricsContainer}>
									<div className={styles.systemMetricsTitleWrapper}>
										<div className={styles.systemMetricsTitleInfoWrapper}>
											<Typography className={styles.systemMetricsTitle}>
												{i18nPackage.t("beforeQuiz.systemCheck.systemResources.memoryUsage")}
											</Typography>
											<CoreInfoIndicator
												hint={i18nPackage.t("beforeQuiz.systemCheck.systemResources.memoryUsageTooltip")}
												size={20}
												color={"#565656"}
												PopperProps={popperProps}
											/>
										</div>
										<CoreChip variant="rectangular" chipColor={memoryBadgeColor} className={memoryBadgeClassName}>
											{memoryBadgeText}
										</CoreChip>
									</div>
									<Typography
										className={styles.systemMetricsValue}
										color={memoryValueColor}
									>{`${memoryUsage}%`}</Typography>
								</div>
							</div>
						)}
				</div>
			</div>
			{status === "failed" && <SystemResourceGuide />}
		</BasicSubstep>
	);
}

function SystemResourceGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.systemCheck.systemResources.guide")}
			iframeTitle={i18nPackage.t("beforeQuiz.systemCheck.systemResources.videoTitle")}
			src="https://demo.video.yuja.com/V/Video?v=14648911&node=63114455&a=91766571"
			title={i18nPackage.t("beforeQuiz.systemCheck.systemResources.guideTitle")}
		/>
	);
}

export function convertMetersToFeet(meters: number, roundDigits?: number) {
	const finalRoundDigits = roundDigits ?? 0;
	return Number((meters * 3.281).toFixed(finalRoundDigits));
}

export function convertFeetToMeters(feet: number, roundDigits?: number) {
	const finalRoundDigits = roundDigits ?? 0;
	return Number((feet / 3.281).toFixed(finalRoundDigits));
}

export type EnableGeofenceFailReason = "noPermission" | "unknown" | "timeout" | "notInRadius";

export function convertGeolocationPositionErrorToFailReason(error?: unknown): EnableGeofenceFailReason | undefined {
	if (!error) return undefined;

	const isGeolocationError = typeof error === "object" && "code" in error && typeof error.code === "number";

	if (isGeolocationError) {
		switch (error.code) {
			case 1:
				return "noPermission";
			case 3:
				return "timeout";
			case 2:
			default:
				return "unknown";
		}
	}
	return "unknown";
}

function GeofenceGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.systemCheck.enableGeofence.guide.description")}
			iframeTitle={i18nPackage.t("beforeQuiz.systemCheck.enableGeofence.guide.video")}
			src="https://demo.video.yuja.com/V/Video?v=15049145&node=64310663&a=1052576"
			title={i18nPackage.t("beforeQuiz.systemCheck.enableGeofence.guide.title")}
		/>
	);
}

function getGeofenceDescription(status: SubstepStatus, failReason?: EnableGeofenceFailReason) {
	if (status !== "failed") {
		const translationKey = status === "started" ? "unstarted" : status;
		return i18nPackage.t(`beforeQuiz.systemCheck.enableGeofence.description.${translationKey}`);
	}

	// For now, timeout = unknown
	const failReasonKey = failReason ? (failReason === "timeout" ? "unknown" : failReason) : "unknown";
	return i18nPackage.t(`beforeQuiz.systemCheck.enableGeofence.description.${failReasonKey}`);
}

function getGeofenceTitle(status: SubstepStatus, failReason?: EnableGeofenceFailReason) {
	if (status !== "failed") {
		const translationKey = status === "started" ? "unstarted" : status;
		return i18nPackage.t(`beforeQuiz.systemCheck.enableGeofence.title.${translationKey}`);
	}

	return failReason === "notInRadius"
		? i18nPackage.t("beforeQuiz.systemCheck.enableGeofence.title.notInRadius")
		: i18nPackage.t("beforeQuiz.systemCheck.enableGeofence.title.unknownLocation");
}

type EnableGeofenceSubstepProps = BaseSubstepProps & {
	onTryAgain: () => void;
	failReason?: EnableGeofenceFailReason;
};

export function EnableGeofenceSubstep({ status, onTryAgain, rootRef, failReason }: EnableGeofenceSubstepProps) {
	const title = getGeofenceTitle(status, failReason);
	const description = getGeofenceDescription(status, failReason);

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.systemCheckSubstep}>
				{getBasicSubstepIcon(status)}
				<div className={styles.systemCheckContent}>
					<BasicSubstepTitle title={title} />
					<div className={styles.systemCheckButtonRow}>
						<BasicSubstepDescription description={description} />
						{status === "failed" && (
							<CoreButton className={styles.tryAgainButton} onClick={onTryAgain}>
								{i18nPackage.t("beforeQuiz.systemCheck.enableGeofence.tryAgainButton")}
							</CoreButton>
						)}
					</div>
				</div>
			</div>
			{status === "failed" && (failReason === "noPermission" || failReason === "unknown") && <GeofenceGuide />}
		</BasicSubstep>
	);
}

// Made these functions so that we only update one place for failed threshold
export function hasCpuUsageFailed(cpuUsage: number | undefined) {
	return cpuUsage && cpuUsage >= 90;
}

export function hasMemoryUsageFailed(memoryUsage: number | undefined) {
	return memoryUsage && memoryUsage >= 90;
}

export default function SystemCheckStep({ children }: { children: React.ReactNode }) {
	return <BasicStep description={i18nPackage.t("beforeQuiz.systemCheck.description")}>{children}</BasicStep>;
}
